import React, { useEffect, useMemo, useState } from "react";
import { notification } from "antd";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase-config";
import Progress from "./progress/Progress";
import { startOfWeek, endOfWeek, subDays, format } from "date-fns";
import { Users, TrendingUp, Activity, Clock } from "lucide-react";
import GraphCard from "../../../../components/ChartKit/GraphCard"; // <— new
const goldGrad = "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41]";
const cardBase =
  "rounded-2xl border border-white/10 bg-gradient-to-b from-[#0E0E0E] to-black";

function StatCard({ title, value, delta, icon: Icon }) {
  return (
    <div className={`${cardBase} p-4 sm:p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">{title}</div>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center border border-white/10 bg-black/40">
          <Icon className="h-5 w-5 text-[#CF9F41]" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {delta && (
        <div
          className={`mt-1 text-xs ${
            delta < 0 ? "text-red-500" : "text-emerald-300"
          }`}
        >
          {delta < 0 ? "↓" : "↑"} {Math.abs(delta) + "%"}{" "}
          {delta < 0 ? "down" : "up"} from yesterday
        </div>
      )}
    </div>
  );
}

/* tiny deterministic series so UI looks good */
const useSeries = (seed = 1, n = 12, base = 50, swing = 25) =>
  useMemo(() => {
    const a = [];
    let x = seed;
    for (let i = 0; i < n; i++) {
      x = (x * 9301 + 49297) % 233280;
      const v = base + ((x / 233280) * 2 - 1) * swing;
      a.push(Math.max(2, Math.round(v)));
    }
    return a;
  }, [seed, n, base, swing]);

export default function TrainerDashboard() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthly = useSeries(3, 12, 50, 30);
  const weekly = useSeries(7, 12, 40, 35);
  const weekLabels = [
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
  ];

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "lms_package"));
        if (cancel) return;
        setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        notification.error({
          message: "Failed to load packages",
          placement: "topRight",
        });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalUsersGrowth: 0,
    activeToday: 0,
    activeTodayGrowth: 0,
    newThisWeek: 0,
    newThisWeekGrowth: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        if (snapshot.empty) return;

        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        //start of today
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        //yesterday
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        const endOfYesterday = new Date(startOfToday);
        endOfYesterday.setMilliseconds(-1);

        // start of week
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - 7);
        startOfWeek.setHours(0, 0, 0, 0);

        const totalUsers = users.length;

        // Yesterday total: created before today
        const totalUsersYesterday = users.filter((u) => {
          if (!u.createdAt) return false;
          const [day, month, year] = u.createdAt.split(" ")[0].split("-");
          const createdDate = new Date(`20${year}-${month}-${day}`);
          return createdDate < startOfToday;
        }).length;

        const totalUsersGrowth = totalUsersYesterday
          ? ((totalUsers - totalUsersYesterday) / totalUsersYesterday) * 100
          : 0;

        //Active Today
        // placeholder: 0 (implement lastActive later)
        const activeToday = 0;
        const activeYesterday = 0;
        const activeTodayGrowth = activeYesterday
          ? ((activeToday - activeYesterday) / activeYesterday) * 100
          : 0;

        //New This Week
        const newThisWeek = users.filter((u) => {
          if (!u.createdAt) return false;
          const [day, month, year] = u.createdAt.split(" ")[0].split("-");
          const createdDate = new Date(`20${year}-${month}-${day}`);
          return createdDate >= startOfWeek;
        }).length;

        const newThisWeekYesterday = users.filter((u) => {
          if (!u.createdAt) return false;
          const [day, month, year] = u.createdAt.split(" ")[0].split("-");
          const createdDate = new Date(`20${year}-${month}-${day}`);
          return createdDate < startOfWeek;
        }).length;

        const newThisWeekGrowth = newThisWeekYesterday
          ? ((newThisWeek - newThisWeekYesterday) / newThisWeekYesterday) * 100
          : 0;

        setStats({
          totalUsers,
          totalUsersGrowth: totalUsersGrowth.toFixed(1),
          activeToday,
          activeTodayGrowth: activeTodayGrowth.toFixed(1),
          newThisWeek,
          newThisWeekGrowth: newThisWeekGrowth.toFixed(1),
        });
      } catch (e) {
        console.error("Error fetching user stats:", e);
      }
    };
    fetchStats();
  }, []);

  const [threeDayData, setThreeDayData] = useState([]);

  useEffect(() => {
    const fetchUsersJoinedEvery3Days = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        if (snapshot.empty) return;

        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Helper to parse createdAt string (format: DD-MM-YY HH:MM)
        const parseDate = (createdAt) => {
          const [day, month, year] = createdAt.split(" ")[0].split("-");
          return new Date(`20${year}-${month}-${day}`);
        };

        // Today at midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const periodsCount = 12; // 12 periods of 3 days each
        const periods = [];

        for (let i = periodsCount - 1; i >= 0; i--) {
          const start = new Date(today);
          start.setDate(today.getDate() - (i + 1) * 3);
          start.setHours(0, 0, 0, 0);

          const end = new Date(today);
          end.setDate(today.getDate() - i * 3);
          end.setHours(0, 0, 0, 0);

          periods.push({ start, end });
        }

        // Count new users per 3-day period
        const newUsersPerPeriod = periods.map(
          ({ start, end }) =>
            users.filter(
              (u) =>
                u.createdAt &&
                parseDate(u.createdAt) >= start &&
                parseDate(u.createdAt) < end
            ).length
        );

        setThreeDayData(newUsersPerPeriod);
      } catch (e) {
        console.error("Error fetching user stats:", e);
      }
    };

    fetchUsersJoinedEvery3Days();
  }, []);

  const [amountstats, setAmountStats] = useState({
    totalRevenue: 0,
    dailyRevenue: {},
    todayRevenue: 0,
    yesterdayRevenue: 0,
    growth: "0%",
  });

  useEffect(() => {
    const fetchRevenueStats = async () => {
      try {
        const snapshot = await getDocs(collection(db, "transactions"));
        if (snapshot.empty) {
          setStats((prev) => ({ ...prev }));
          return;
        }

        const transactions = snapshot.docs.map((doc) => ({
          ...doc.data(),
          amount: Number(doc.data().amount),
          createdAt: doc.data().createdAt.toDate
            ? doc.data().createdAt.toDate()
            : new Date(doc.data().createdAt),
        }));

        // ---- Total revenue ----
        const totalRevenue = transactions.reduce((sum, t) => sum + t.amount, 0);

        // ---- Weekly revenue by day ----
        const now = new Date();
        const start = startOfWeek(now, { weekStartsOn: 1 });
        const end = endOfWeek(now, { weekStartsOn: 1 });

        const weekTransactions = transactions.filter(
          (t) => t.createdAt >= start && t.createdAt <= end
        );

        const dailyRevenue = {};
        for (let d = 0; d < 12; d++) {
          const day = new Date(start);
          day.setDate(start.getDate() + d);
          const dayKey = format(day, "yyyy-MM-dd");

          const totalForDay = weekTransactions
            .filter((tx) => format(tx.createdAt, "yyyy-MM-dd") === dayKey)
            .reduce((sum, tx) => sum + tx.amount, 0);

          dailyRevenue[dayKey] = totalForDay;
        }

        // ---- Yesterday vs Today growth ----
        const todayKey = format(now, "yyyy-MM-dd");
        const yesterdayKey = format(subDays(now, 1), "yyyy-MM-dd");

        const todayRevenue = dailyRevenue[todayKey] || 0;
        const yesterdayRevenue = dailyRevenue[yesterdayKey] || 0;

        let growthStr = "0%";
        if (yesterdayRevenue === 0 && todayRevenue === 0) {
          growthStr = "0%";
        } else if (yesterdayRevenue === 0 && todayRevenue > 0) {
          growthStr = "↑ 100%";
        } else {
          const growth =
            ((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100;
          const rounded = growth.toFixed(2);
          growthStr = growth >= 0 ? `↑ ${rounded}%` : `↓ ${Math.abs(rounded)}%`;
        }

        setAmountStats({
          totalRevenue,
          dailyRevenue,
          todayRevenue,
          yesterdayRevenue,
          growth: growthStr,
        });
      } catch (err) {
        console.error("Error fetching transactions:", err);
      }
    };

    fetchRevenueStats();
  }, []);
  const allweekLabels = Object.keys(amountstats.dailyRevenue);
  const revenueData = Object.values(amountstats.dailyRevenue);
  return (
    <div className="min-h-screen bg-[#0B0B0B] text-zinc-100 px-3 sm:px-4 py-4">
      {/* Title */}
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-[#F3E681] to-[#B17E32]" />
            <h1 className="text-2xl sm:text-3xl font-semibold">
              Education &amp; Progress
            </h1>
          </div>
          <span
            className={`${goldGrad} text-black text-[11px] font-bold px-3 py-1 rounded-full hidden sm:inline-flex`}
          >
            Trainer
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-auto max-w-6xl mt-4 grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          delta={stats.totalUsersGrowth}
          icon={Users}
        />
        <StatCard
          title="Active Today"
          value={stats.activeToday}
          delta={stats.activeTodayGrowth}
          icon={Activity}
        />
        <StatCard
          title="New This Week"
          value={stats.newThisWeek}
          delta={stats.newThisWeekGrowth}
          icon={TrendingUp}
        />
        <StatCard
          title="Avg. Class Time"
          value="52m"
          delta="2.2%"
          icon={Clock}
        />
      </div>
      {/* Graphs via GraphCard (customizable) */}
      <div className="mx-auto max-w-6xl mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <GraphCard
          title="This month"
          subtitle="New users every 3 days"
          chip="Live"
          type="spark" // keep sparkline
          data={threeDayData} // 12-period data
          sparkHeight={150}
          sparkStroke={3}
          sparkShowDots
          footer="Tip: hover the link icon near a module to copy its join URL quickly."
        />

        <GraphCard
          title="Weekly Revenue"
          subtitle="overview"
          type="bars"
          data={revenueData}
          labels={allweekLabels}
          barsMaxHeight={120}
          right={
            <span className="text-[11px] text-zinc-400">updated 3m ago</span>
          }
        />
      </div>
      {/* Courses / Progress (kept) */}
      <div className="mx-auto max-w-6xl mt-5">
        <div className={`${cardBase} p-3 sm:p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#F3E681]" />
              <h2 className="text-sm sm:text-base font-semibold">
                Your courses
              </h2>
            </div>
            <span
              className={`${goldGrad} text-black text-[11px] font-bold px-2.5 py-1 rounded-full`}
            >
              {loading ? "Loading…" : `${packages.length} packages`}
            </span>
          </div>

          {loading ? (
            <div className="grid gap-2">
              <div className="h-12 rounded-lg bg-white/5 animate-pulse" />
              <div className="h-12 rounded-lg bg-white/5 animate-pulse" />
              <div className="h-12 rounded-lg bg-white/5 animate-pulse" />
            </div>
          ) : packages.length === 0 ? (
            <div className="grid place-items-center py-10 text-sm text-zinc-400 border border-dashed border-white/10 rounded-lg">
              No packages found for you yet.
            </div>
          ) : (
            <div className="mt-2">
              <Progress data={packages} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
